import {
    createContext,
    type Dispatch,
    type PropsWithChildren,
    type SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { type MenuItem } from '@ics-portal/guardian';
import type {
    ColDef,
    ColGroupDef,
    GridApi,
    ICellRendererParams,
    RowGroupOpenedEvent,
} from '@ag-grid-community/core';

type DetailCellRendererParamsOrFn<TData> =
    | { contextMenuItems?: (params: ICellRendererParams<TData>) => MenuItem[] }
    | ((params: ICellRendererParams) => {
          contextMenuItems?: (params: ICellRendererParams<TData>) => MenuItem[];
      });

export interface SearchTarget {
    api: GridApi;
    columnDefs: (ColDef | ColGroupDef)[];
}

// Read context — changes when items change (row selection etc.)
// Only ICSGridActions subscribes to this.
const ICSGridActionsReadContext = createContext<MenuItem[]>([]);

interface ICSGridActionsWriteContextValue {
    setMasterItems: Dispatch<SetStateAction<MenuItem[]>>;
    setDetailItems: Dispatch<SetStateAction<MenuItem[] | null>>;
    setMasterSelectedRowsCount: Dispatch<SetStateAction<number>>;
    registerChildItems: (id: string, items: MenuItem[] | null) => void;
}

// Write context — holds stable callbacks, never changes after mount.
// Child grids subscribe only to this, so they are never re-rendered by
// menu-item or row-selection changes in the master grid.
const ICSGridActionsWriteContext =
    createContext<ICSGridActionsWriteContextValue>({
        setMasterItems: () => {},
        setDetailItems: () => {},
        setMasterSelectedRowsCount: () => {},
        registerChildItems: () => {},
    });

// Split into read/write contexts so only ICSGridControls re-renders on target
// changes — child grids subscribe only to the write context whose value (a
// stable useCallback) never changes, so they are never re-rendered by it.
const ICSGridSearchReadContext = createContext<SearchTarget | null>(null);
const ICSGridSearchWriteContext = createContext<
    (id: string, target: SearchTarget | null) => void
>(() => {});

export function ICSGridActionsProvider({ children }: PropsWithChildren) {
    const [childMenuItems, setChildMenuItems] = useState<MenuItem[] | null>(
        null,
    );
    const [masterItems, setMasterItems] = useState<MenuItem[]>([]);
    const [detailItems, setDetailItems] = useState<MenuItem[] | null>(null);
    const [masterSelectedRowsCount, setMasterSelectedRowsCount] = useState(0);
    const [detailSearchTarget, setDetailSearchTarget] =
        useState<SearchTarget | null>(null);

    // Tracks which child instance last registered — prevents stale unmount
    // cleanup from a previously expanded row from wiping the new row's items
    const childOwnerRef = useRef<string | null>(null);
    const registerChildItems = useCallback(
        (id: string, items: MenuItem[] | null) => {
            if (items === null && childOwnerRef.current !== id) return;
            childOwnerRef.current = items !== null ? id : null;
            setChildMenuItems(items);
        },
        [],
    );

    const detailSearchOwnerRef = useRef<string | null>(null);
    const registerDetailSearchTarget = useCallback(
        (id: string, target: SearchTarget | null) => {
            if (target === null && detailSearchOwnerRef.current !== id) return;
            detailSearchOwnerRef.current = target !== null ? id : null;
            setDetailSearchTarget(target);
        },
        [],
    );

    // Rule priority:
    // 2.   Master has row selection → masterItems (overrides nested, explicit row action takes precedence)
    // 3/4. Nested table active → childMenuItems (with child selectedRows) or detailItems
    // 1.   No selection, no nested → masterItems (general table actions)
    const items = (
        masterSelectedRowsCount > 0
            ? masterItems
            : childMenuItems ?? detailItems ?? masterItems
    ).filter(({ disabled }) => !disabled);

    return (
        <ICSGridActionsWriteContext.Provider
            value={{
                setMasterItems,
                setDetailItems,
                setMasterSelectedRowsCount,
                registerChildItems,
            }}
        >
            <ICSGridActionsReadContext.Provider value={items}>
                <ICSGridSearchWriteContext.Provider
                    value={registerDetailSearchTarget}
                >
                    <ICSGridSearchReadContext.Provider
                        value={detailSearchTarget}
                    >
                        {children}
                    </ICSGridSearchReadContext.Provider>
                </ICSGridSearchWriteContext.Provider>
            </ICSGridActionsReadContext.Provider>
        </ICSGridActionsWriteContext.Provider>
    );
}

// Internal — used by ICSGridActions to read resolved items
export function useICSGridActionsContext(): MenuItem[] {
    return useContext(ICSGridActionsReadContext);
}

// Used by ICSGridControls to read the active search target
export function useDetailSearchTarget(): SearchTarget | null {
    return useContext(ICSGridSearchReadContext);
}

// Used by ICSGridActions to register master grid state into context
export function useRegisterMasterGrid<TData>(
    api: GridApi<TData> | undefined,
    context: unknown,
    contextMenuItems:
        | ((params: ICellRendererParams<TData>) => MenuItem[])
        | undefined,
    detailCellRendererParams: DetailCellRendererParamsOrFn<TData> | undefined,
): void {
    const { setMasterItems, setDetailItems, setMasterSelectedRowsCount } =
        useContext(ICSGridActionsWriteContext);
    const registerDetailSearchTarget = useContext(ICSGridSearchWriteContext);
    const expandedNodeIdRef = useRef<string | null>(null);

    const masterItems = useMemo<MenuItem[]>(() => {
        if (!api || !contextMenuItems || !context) return [];
        return contextMenuItems({
            context: context as unknown,
            api,
        } as unknown as ICellRendererParams<TData>);
    }, [api, context, contextMenuItems]);

    const masterSelectedRowsCount = useMemo(
        () =>
            (context as { selectedRows?: unknown[] } | undefined)?.selectedRows
                ?.length ?? 0,
        [context],
    );

    useEffect(() => {
        setMasterItems(masterItems);
    }, [masterItems, setMasterItems]);

    useEffect(() => {
        setMasterSelectedRowsCount(masterSelectedRowsCount);
    }, [masterSelectedRowsCount, setMasterSelectedRowsCount]);

    useEffect(() => {
        if (!api || !detailCellRendererParams) return;

        const handleRowGroupOpened = (
            event: RowGroupOpenedEvent<TData>,
        ): void => {
            if (!event.node.master) return;

            if (!event.expanded) {
                registerDetailSearchTarget(
                    expandedNodeIdRef.current ?? 'config-detail',
                    null,
                );
                expandedNodeIdRef.current = null;
                setDetailItems(null);
                return;
            }

            expandedNodeIdRef.current = event.node.id ?? null;

            const resolvedParams =
                typeof detailCellRendererParams === 'function'
                    ? detailCellRendererParams(
                          event as unknown as ICellRendererParams,
                      )
                    : detailCellRendererParams;

            const detailContextMenuItems = resolvedParams.contextMenuItems;

            // Detail grid initialises asynchronously — defer by one frame
            setTimeout(() => {
                if (expandedNodeIdRef.current !== event.node.id) return;
                api.forEachDetailGridInfo((info) => {
                    if (!info.api) return;
                    registerDetailSearchTarget(
                        event.node.id ?? 'config-detail',
                        {
                            api: info.api,
                            columnDefs: info.api.getColumnDefs() ?? [],
                        },
                    );
                    if (!detailContextMenuItems) return;
                    setDetailItems(
                        detailContextMenuItems({
                            context: info.api.getGridOption(
                                'context',
                            ) as unknown,
                            api: info.api,
                        } as unknown as ICellRendererParams<TData>),
                    );
                });
            }, 0);
        };

        api.addEventListener('rowGroupOpened', handleRowGroupOpened);
        return () => {
            if (!api.isDestroyed())
                api.removeEventListener('rowGroupOpened', handleRowGroupOpened);
        };
    }, [
        api,
        detailCellRendererParams,
        setDetailItems,
        registerDetailSearchTarget,
    ]);

    useEffect(
        () => () => {
            setMasterItems([]);
            setDetailItems(null);
            setMasterSelectedRowsCount(0);
        },
        [setMasterItems, setDetailItems, setMasterSelectedRowsCount],
    );
}

// Used by child ICSAgGrid instances to push items up — auto-clears on unmount
export function useChildGridActions(): {
    setChildMenuItems: (items: MenuItem[] | null) => void;
    setChildSearchTarget: (target: SearchTarget | null) => void;
} {
    const { registerChildItems } = useContext(ICSGridActionsWriteContext);
    const registerDetailSearchTarget = useContext(ICSGridSearchWriteContext);
    const idRef = useRef(`child-${Math.random()}`);

    useEffect(
        () => () => {
            registerChildItems(idRef.current, null);
            registerDetailSearchTarget(idRef.current, null);
        },
        [registerChildItems, registerDetailSearchTarget],
    );

    const setChildMenuItems = useCallback(
        (items: MenuItem[] | null) => {
            registerChildItems(idRef.current, items);
        },
        [registerChildItems],
    );

    const setChildSearchTarget = useCallback(
        (target: SearchTarget | null) => {
            registerDetailSearchTarget(idRef.current, target);
        },
        [registerDetailSearchTarget],
    );

    return { setChildMenuItems, setChildSearchTarget };
}
